import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import LanguageToggle from './LanguageToggle';
import UserProfileMenu from './UserProfileMenu';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const navigationItems = [
  {
    id: 'dashboard',
    labelEn: 'Dashboard',
    labelFr: 'Tableau de bord',
    labelAr: 'لوحة القيادة',
    icon: 'LayoutDashboard',
    path: '/dashboard',
    requiredRole: ['lawyer', 'secretary', 'administrator']
  },
  {
    id: 'clients',
    labelEn: 'Clients',
    labelFr: 'Clients',
    labelAr: 'العملاء',
    icon: 'Users',
    path: '/client-management',
    requiredRole: ['lawyer', 'secretary', 'administrator']
  },
  {
    id: 'cases',
    labelEn: 'Cases',
    labelFr: 'Dossiers',
    labelAr: 'القضايا',
    icon: 'Briefcase',
    path: '/case-management',
    requiredRole: ['lawyer', 'secretary', 'administrator'],
    hasNotifications: true,
    notificationCount: 3
  },
  {
    id: 'calendar',
    labelEn: 'Calendar',
    labelFr: 'Calendrier',
    labelAr: 'التقويم',
    icon: 'Calendar',
    path: '/calendar-scheduling',
    requiredRole: ['lawyer', 'secretary', 'administrator'],
    hasNotifications: true,
    notificationCount: 5
  },
  {
    id: 'documents',
    labelEn: 'Documents',
    labelFr: 'Documents',
    labelAr: 'المستندات',
    icon: 'FileText',
    path: '/document-management',
    requiredRole: ['lawyer', 'secretary', 'administrator']
  },
  {
    id: 'billing',
    labelEn: 'Billing',
    labelFr: 'Facturation',
    labelAr: 'الفواتير',
    icon: 'Receipt',
    path: '/billing-invoice',
    requiredRole: ['lawyer', 'administrator'],
    hasNotifications: true,
    notificationCount: 2
  },
  {
    id: 'administration',
    labelEn: 'Administration',
    labelFr: 'Administration',
    labelAr: 'الإدارة',
    icon: 'Settings',
    path: '/user-administration',
    requiredRole: ['administrator']
  }];


  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  const [direction, setDirection] = useState(() => {
    return localStorage.getItem('direction') || 'ltr';
  });

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    const newDirection = newLanguage === 'ar' ? 'rtl' : 'ltr';
    setDirection(newDirection);
    localStorage.setItem('language', newLanguage);
    localStorage.setItem('direction', newDirection);
    document.documentElement?.setAttribute('dir', newDirection);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const getLabel = (item) => {
    if (language === 'fr') return item?.labelFr;
    if (language === 'ar') return item?.labelAr;
    return item?.labelEn;
  };

  const isActive = (path) => location?.pathname === path;

  return (
    <>
      {/* Mobile Dropdown Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-[60]" ref={dropdownRef}>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="bg-primary text-white p-3 rounded-lg shadow-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
          aria-label="Toggle menu"
        >
          <Icon name="Menu" size={20} />
          <span className="text-sm font-medium">Menu</span>
        </button>

        {/* Mobile Dropdown Menu */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 mt-2 w-72 bg-card border border-border rounded-lg shadow-xl overflow-hidden max-h-[calc(100vh-100px)] overflow-y-auto">
            {/* Language Toggle in Dropdown */}
            <div className="p-3 border-b border-border">
              <LanguageToggle
                currentLanguage={language}
                onLanguageChange={handleLanguageChange}
                isCollapsed={false}
              />
            </div>

            {/* Navigation Items */}
            <nav className="py-2">
              {navigationItems?.map((item) => (
                <div
                  key={item?.id}
                  className={`flex items-center gap-3 px-4 py-3 text-card-foreground hover:bg-muted cursor-pointer transition-colors ${
                    isActive(item?.path) ? 'bg-primary text-primary-foreground' : ''
                  }`}
                  onClick={() => handleNavigate(item?.path)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e?.key === 'Enter') handleNavigate(item?.path);
                  }}
                >
                  <div className="relative">
                    <Icon name={item?.icon} size={20} />
                    {item?.hasNotifications && item?.notificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                        {item?.notificationCount}
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-medium">{getLabel(item)}</span>
                </div>
              ))}
            </nav>

            {/* User Profile in Dropdown */}
            <div className="border-t border-border p-3">
              <UserProfileMenu
                language={language}
                direction={direction}
                isCollapsed={false}
              />
            </div>
          </div>
        )}
      </div>

      {/* Desktop Sidebar - Always Visible */}
      <aside
        className="sidebar hidden lg:flex lg:flex-col"
        style={{ direction }}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Icon name="Scale" size={24} color="#FFFFFF" />
          </div>
          <span className="sidebar-logo-text mb-[-3px] pl-1.5 pr-6">Cabinet Maitre Moatamid</span>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-3 py-2">
            <LanguageToggle
              currentLanguage={language}
              onLanguageChange={handleLanguageChange}
              isCollapsed={isCollapsed}
            />
          </div>

          <nav className="sidebar-nav">
            {navigationItems?.map((item) => (
              <div
                key={item?.id}
                className={`sidebar-nav-item ${
                  isActive(item?.path) ? 'active' : ''
                }`}
                onClick={() => handleNavigate(item?.path)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e?.key === 'Enter') handleNavigate(item?.path);
                }}
              >
                <div className="relative">
                  <Icon name={item?.icon} size={20} />
                  {item?.hasNotifications && item?.notificationCount > 0 && (
                    <span className="notification-badge">
                      {item?.notificationCount}
                    </span>
                  )}
                </div>
                <span className="sidebar-nav-item-text">{getLabel(item)}</span>
              </div>
            ))}
          </nav>
        </div>

        <div className="sidebar-footer">
          <UserProfileMenu
            language={language}
            direction={direction}
            isCollapsed={isCollapsed}
          />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;